import React, { FC, useCallback, useState } from 'react'
import cx from 'classnames'
import { useDispatch, useSelector } from 'react-redux'
import { useEffectOnce, useHover } from 'react-use'
import Button from 'views/components/atoms/Button/Button'
import Switch from 'views/components/atoms/Switch/Switch'
import Tooltip from 'views/components/atoms/Tooltip/Tooltip'
import DownloadIcon from 'views/components/atoms/Icons/Doc/DocumentDownload'
import { apiStateSelectors } from 'modules/api/apiState'
import { tpnApiEvents, tpnApiSelectors } from 'modules/api/tpn'
import { userApiSelectors } from 'modules/api/user'
import Logo from 'theme/images/logo-tpn.png'
import { formatDate } from 'utils/date'
import TpnSettingsModal from './TpnSettingsModal'
import DownloadTpnReportModal from './DownloadTpnReportModal'
import styles from './TpnReportCard.module.scss'

type Props = {
  name: string
  short: string
}

/**
 * TPN report card: CSV download for everyone, and for owners and team
 * members an Enabled/Disabled switch into `TpnSettingsModal` (connect or
 * disconnect) plus Send now once connected. Backed by le-go's /reports/tpn/*.
 */
const TpnReportCard: FC<Props> = ({ name, short }) => {
  const dispatch = useDispatch()

  useEffectOnce(() => {
    dispatch(tpnApiEvents.tpnSettings_request())
  })

  const loaded = useSelector(tpnApiSelectors.isTpnSettingsLoaded)
  const loadFailed = useSelector(tpnApiSelectors.didTpnSettingsLoadFail)
  const isConnected = useSelector(tpnApiSelectors.isTpnConnected)
  const lastSend = useSelector(tpnApiSelectors.getTpnLastSend)
  const isOwnerRole = useSelector(userApiSelectors.isOwnerRole)
  const isTeamMemberRole = useSelector(userApiSelectors.isTeamMemberRole)
  const canManage = isOwnerRole || isTeamMemberRole
  const isSending = useSelector(state => apiStateSelectors.isLoading(state)([tpnApiEvents.sendTpnReport_request]))

  const [isSettingsOpen, setIsSettingsOpen] = useState(false)
  const [isDownloadOpen, setIsDownloadOpen] = useState(false)

  const [hoverableDownload, isHoveringDownload] = useHover(
    <button type="button" className={styles.download} onClick={() => setIsDownloadOpen(true)} aria-label="Download">
      <DownloadIcon />
    </button>,
  )

  const statusLine = lastSend && (
    <p className={styles.status}>
      <span className={cx(styles.dot, styles[`dot--${lastSend.status}`])} />
      {lastSend.status === 'sent' ? 'Last sent' : 'Last send failed'} {formatDate(lastSend.sentAt)} ({lastSend.trigger})
    </p>
  )

  const closeDownload = useCallback(() => setIsDownloadOpen(false), [])

  return (
    <>
      <div className={styles.body}>
        <figure>
          <img src={Logo} alt={name} />
        </figure>
        <div className={styles.content}>
          <h3>{name}</h3>
          <p>{short}</p>
        </div>
        <div className={styles.actions}>
          {canManage && loaded && !loadFailed ? (
            <div className={styles.manage}>
              <Switch
                className="integration-toggle"
                label={isConnected ? 'Enabled' : 'Disabled'}
                labelHint="We send your data to TPN on the 5th, 10th and 20th of each month, as recommended by TPN."
                checked={isConnected}
                onChange={() => setIsSettingsOpen(true)}
              />
              {isConnected && (
                <Button loading={isSending} size="sm" onClick={() => dispatch(tpnApiEvents.sendTpnReport_request())}>
                  Send now
                </Button>
              )}
            </div>
          ) : (
            <span />
          )}
          {!isConnected && (
            <Tooltip isOpen={isHoveringDownload} body="Download CSV">
              {hoverableDownload}
            </Tooltip>
          )}
        </div>
        {statusLine}
      </div>

      <DownloadTpnReportModal isOpen={isDownloadOpen} onClose={closeDownload} />
      {canManage && <TpnSettingsModal isOpen={isSettingsOpen} onClose={() => setIsSettingsOpen(false)} />}
    </>
  )
}

export default TpnReportCard
